import React, { useState, useEffect } from "react";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Loader2Icon, InfoIcon, DatabaseIcon, AppWindowIcon } from "lucide-react";
import { cn } from "../../../../lib/utils";

interface CompositionBaseTypeProps {
  baseType?: "intent_group" | "application_intent";
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
  onChange: (data: Partial<{
    baseType: "intent_group" | "application_intent";
    intentGroupId?: number;
    applicationId?: number;
    intentId?: number;
  }>) => void;
}

const CompositionBaseType: React.FC<CompositionBaseTypeProps> = ({
  baseType,
  intentGroupId,
  applicationId,
  intentId,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loadingIntentGroups, setLoadingIntentGroups] = useState<boolean>(false);
  const [loadingApplications, setLoadingApplications] = useState<boolean>(false);
  const [loadingIntents, setLoadingIntents] = useState<boolean>(false);
  const [intentGroups, setIntentGroups] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [intents, setIntents] = useState<any[]>([]);

  // Fetch intent groups
  useEffect(() => {
    if (baseType === "intent_group" && apiKey) {
      setLoadingIntentGroups(true);
      IntentService.getIntentGroups(
        0,
        100,
        setLoadingIntentGroups,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent groups response:", response);
          if (response && Array.isArray(response)) {
            setIntentGroups(response);
          } else if (response && Array.isArray(response.intent_groups)) {
            setIntentGroups(response.intent_groups);
          } else {
            console.error(
              "Unexpected intent groups response format:",
              response
            );
            setIntentGroups([]);
          }
          setLoadingIntentGroups(false);
        },
        (error: any) => {
          console.error("Error fetching intent groups:", error);
          setLoadingIntentGroups(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch applications
  useEffect(() => {
    if (baseType === "application_intent" && apiKey) {
      setLoadingApplications(true);
      ApplicationService.getApplications(
        setLoadingApplications,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Applications response:", response);
          if (response && Array.isArray(response)) {
            setApplications(response);
          } else if (response && Array.isArray(response.items)) {
            setApplications(response.items);
          } else {
            console.error("Unexpected applications response format:", response);
            setApplications([]);
          }
          setLoadingApplications(false);
        },
        (error: any) => {
          console.error("Error fetching applications:", error);
          setLoadingApplications(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch intents for selected application
  useEffect(() => {
    if (baseType === "application_intent" && applicationId && apiKey) {
      setLoadingIntents(true);
      setIntents([]); // Clear previous intents when application changes
      console.log("Fetching intents for application ID:", applicationId);
      
      ApplicationService.getApplicationActions(
        applicationId,
        setLoadingIntents,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intents response:", response);
          if (response && Array.isArray(response)) {
            setIntents(response);
          } else if (response && Array.isArray(response.items)) {
            setIntents(response.items);
          } else {
            console.error("Unexpected intents response format:", response);
            setIntents([]);
          }
          setLoadingIntents(false);
        },
        (error: any) => {
          console.error("Error fetching intents:", error);
          setLoadingIntents(false);
        }
      );
    } else {
      // Clear intents if no application is selected
      setIntents([]);
    }
  }, [baseType, applicationId, apiKey]);

  const handleBaseTypeChange = (value: "intent_group" | "application_intent") => {
    onChange({
      baseType: value,
      intentGroupId: undefined,
      applicationId: undefined,
      intentId: undefined,
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Choose Base Type
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Select whether to use an Intent Group or an Application/Intent as the base for your composition
        </p>
      </div>

      {/* Base Type Selection */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <DatabaseIcon className="w-5 h-5 text-maitai-green" />
            Base Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={baseType}
            onValueChange={handleBaseTypeChange}
            className="space-y-4"
          >
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="intent_group" id="intent_group" />
              <div className="space-y-1">
                <Label htmlFor="intent_group" className="text-sm font-gc-med text-foreground cursor-pointer">
                  Intent Group
                </Label>
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Groups intents across multiple applications for broader dataset composition
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="application_intent" id="application_intent" />
              <div className="space-y-1">
                <Label htmlFor="application_intent" className="text-sm font-gc-med text-foreground cursor-pointer">
                  Application/Intent
                </Label>
                <p className="text-xs text-muted-foreground font-gc-reg">
                  Target a specific intent within a particular application
                </p>
              </div>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Intent Group Selection */}
      {baseType === "intent_group" && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <AppWindowIcon className="w-5 h-5 text-maitai-green" />
              Intent Group Selection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">
                Intent Group
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select 
                value={intentGroupId?.toString()} 
                onValueChange={(value) => onChange({ intentGroupId: parseInt(value) })}
              >
                <SelectTrigger className={cn(loadingIntentGroups && "cursor-wait")}>
                  <SelectValue placeholder={loadingIntentGroups ? "Loading..." : "Select an intent group"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingIntentGroups ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading intent groups...</span>
                    </div>
                  ) : intentGroups.length === 0 ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="text-sm text-muted-foreground">No intent groups available</span>
                    </div>
                  ) : (
                    intentGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.intent_name || group.name || `Intent Group ${group.id}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select an intent group to filter available datasets
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Application/Intent Selection */}
      {baseType === "application_intent" && (
        <Card className="border-border">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
              <AppWindowIcon className="w-5 h-5 text-maitai-green" />
              Application & Intent Selection
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">
                Application
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select 
                value={applicationId?.toString()} 
                onValueChange={(value) => {
                  console.log("Application selected:", value);
                  onChange({
                    applicationId: parseInt(value),
                    intentId: undefined,
                  });
                }}
              >
                <SelectTrigger className={cn(loadingApplications && "cursor-wait")}>
                  <SelectValue placeholder={loadingApplications ? "Loading..." : "Select an application"} />
                </SelectTrigger>
                <SelectContent>
                  {loadingApplications ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading applications...</span>
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="text-sm text-muted-foreground">No applications available</span>
                    </div>
                  ) : (
                    applications.map((app) => (
                      <SelectItem key={app.id} value={app.id.toString()}>
                        {app.application_name || `Application ${app.id}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose the application containing your target intent
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-gc-med text-foreground">
                Intent
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select 
                value={intentId?.toString()} 
                onValueChange={(value) => {
                  console.log("Intent selected:", value, "for application:", applicationId);
                  onChange({ intentId: parseInt(value), applicationId: applicationId });
                }}
                disabled={!applicationId}
              >
                <SelectTrigger className={cn(
                  loadingIntents && "cursor-wait",
                  !applicationId && "cursor-not-allowed opacity-50"
                )}>
                  <SelectValue placeholder={
                    !applicationId 
                      ? "Select an application first" 
                      : loadingIntents 
                        ? "Loading..." 
                        : "Select an intent"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {loadingIntents ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Loading intents...</span>
                    </div>
                  ) : intents.length === 0 && applicationId ? (
                    <div className="flex items-center justify-center py-4">
                      <span className="text-sm text-muted-foreground">No intents available</span>
                    </div>
                  ) : (
                    intents.map((intent) => (
                      <SelectItem key={intent.id} value={intent.id.toString()}>
                        {intent.action_type || intent.name || `Intent ${intent.id}`}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select the specific intent for dataset filtering
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompositionBaseType; 